import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, HashRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import CookieBanner from "@/components/CookieBanner";
import { HelmetProvider } from "react-helmet-async";
import QuickFillPickerBootstrap from "@/components/QuickFillPickerBootstrap";

// Detect if running inside Electron (preload may be sandboxed on production)
const isElectron =
  typeof window !== "undefined" &&
  (window.electronAPI !== undefined || window.navigator.userAgent.includes("Electron"));

// Use HashRouter for Electron (file:// protocol), BrowserRouter for web
const Router = isElectron ? HashRouter : BrowserRouter;

// Lazy load all route components for code splitting
// Retry wrapper for dynamic imports to handle stale chunk errors after rebuilds
const lazyRetry = (importFn: () => Promise<any>) =>
  lazy(() =>
    importFn().catch(() => {
      // Force reload to get fresh chunks
      window.location.reload();
      return importFn();
    })
  );

const Landing = lazyRetry(() => import("./pages/Landing"));
const Auth = lazyRetry(() => import("./pages/Auth"));
const Onboarding = lazyRetry(() => import("./pages/Onboarding"));
const AppLayout = lazyRetry(() => import("./pages/App"));
const AssetListView = lazyRetry(() => import("./components/AssetListView").then(m => ({ default: m.AssetListView })));
const AllAssetsView = lazyRetry(() => import("./components/AllAssetsView").then(m => ({ default: m.AllAssetsView })));
const AssetDetails = lazyRetry(() => import("./pages/AssetDetails"));
const Settings = lazyRetry(() => import("./pages/Settings"));
const Share = lazyRetry(() => import("./pages/Share"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
const ApiDocs = lazyRetry(() => import("./pages/ApiDocs"));
const McpDocs = lazyRetry(() => import("./pages/McpDocs"));
const Help = lazyRetry(() => import("./pages/Help"));
const PrivacyPolicy = lazyRetry(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazyRetry(() => import("./pages/TermsConditions"));
const Imprint = lazyRetry(() => import("./pages/Imprint"));
const AboutUs = lazyRetry(() => import("./pages/AboutUs"));
const Pricing = lazyRetry(() => import("./pages/Pricing"));

const Applications = lazyRetry(() => import("./pages/Applications"));
const OpenSourceLicenses = lazyRetry(() => import("./pages/OpenSourceLicenses"));
const AcceptInvite = lazyRetry(() => import("./pages/AcceptInvite"));
const AcceptPublicShare = lazyRetry(() => import("./pages/AcceptPublicShare"));
const DesktopSettings = lazyRetry(() => import("./pages/DesktopSettings"));
const HowItWorks = lazyRetry(() => import("./pages/HowItWorks"));
const AssetListDesktop = lazyRetry(() => import("./components/AssetListDesktop"));
const ExtensionAuth = lazyRetry(() => import("./pages/ExtensionAuth"));
const MobileOAuthCallback = lazyRetry(() => import("./pages/MobileOAuthCallback"));
const OAuthCallback = lazyRetry(() => import("./pages/OAuthCallback"));
const SetupQuickFill = lazyRetry(() => import("./pages/SetupQuickFill"));
const AutofillDebug = lazyRetry(() => import("./pages/AutofillDebug"));
const QuickFillPicker = lazyRetry(() => import("./pages/QuickFillPicker"));
const SeoAnalytics = lazyRetry(() => import("./pages/SeoAnalytics"));
const Blog = lazyRetry(() => import("./pages/Blog"));
const BlogPost = lazyRetry(() => import("./pages/BlogPost"));
const FeaturesIndex = lazyRetry(() => import("./pages/FeaturesIndex"));
const FeatureLanding = lazyRetry(() => import("./pages/FeatureLanding"));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <Router>
          <AuthProvider>
            <Suspense fallback={<PageLoader />}>
              <QuickFillPickerBootstrap />
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/onboarding" element={
                  <ProtectedRoute requireOnboarding={false}>
                    <Onboarding />
                  </ProtectedRoute>
                } />
                <Route path="/help" element={<Help />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsConditions />} />
                <Route path="/imprint" element={<Imprint />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/features" element={<FeaturesIndex />} />
                <Route path="/features/:slug" element={<FeatureLanding />} />
                
                <Route path="/applications" element={<Applications />} />
                <Route path="/open-source-licenses" element={<OpenSourceLicenses />} />
                <Route path="/invite/:inviteId" element={<AcceptInvite />} />
                <Route path="/join/:token" element={<AcceptPublicShare />} />
                <Route path="/api-docs" element={<ApiDocs />} />
                <Route path="/mcp-docs" element={<McpDocs />} />
                <Route path="/extension-auth" element={<ExtensionAuth />} />
                <Route path="/auth/mobile-callback" element={<MobileOAuthCallback />} />
                <Route path="/oauth-callback" element={<OAuthCallback />} />
                <Route path="/desktop-settings" element={<DesktopSettings />} />
                <Route path="/setup-quickfill" element={
                  <ProtectedRoute>
                    <SetupQuickFill />
                  </ProtectedRoute>
                } />
                <Route path="/autofill-debug" element={
                  <ProtectedRoute>
                    <AutofillDebug />
                  </ProtectedRoute>
                } />
                <Route path="/quickfill-picker" element={
                  <ProtectedRoute>
                    <QuickFillPicker />
                  </ProtectedRoute>
                } />
                <Route path="/assetlistdesktop" element={
                  <ProtectedRoute>
                    <AssetListDesktop />
                  </ProtectedRoute>
                } />
                <Route path="/app" element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={<AssetListView />} />
                  <Route path="all" element={<AllAssetsView />} />
                  <Route path="list/:id" element={<AssetListView />} />
                  <Route path="asset/:assetId" element={<AssetDetails />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="share" element={<Share />} />
                  <Route path="quick-access" element={<QuickFillPicker />} />
                  <Route path="seo" element={<SeoAnalytics />} />
                </Route>
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <CookieBanner />
          </AuthProvider>
        </Router>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
